import React, { useRef, useState, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import merge from 'lodash/merge';
import objectHash from 'object-hash';
import get from 'lodash/get';

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (e.includes(n)) continue;
    t[n] = r[n];
  }
  return t;
}

var start = {
  id: 'start',
  plugin: ({
    type
  }, context) => {
    const {
      onRequestStart
    } = context.componentContext.getProps();
    const {
      setIsLoading,
      setIsComplete
    } = context.componentContext;
    onRequestStart && onRequestStart();
    if (type === 'refresh') {
      setIsLoading(true);
    }
    setIsComplete(false);
  }
};

var loader = {
  id: 'loader',
  plugin: async (props, context) => {
    const {
      loader
    } = context.componentContext.getProps();
    if (typeof loader !== 'function') {
      return;
    }
    if (context.outputStack['cache']) {
      return context.outputStack['cache'];
    }
    const data = await loader(context.outputStack['params']);
    return {
      data: {
        code: 200,
        results: data
      }
    };
  },
  dependencies: ['params']
};

const _excluded$3 = ["options", "urlParams"];
var params = {
  id: 'params',
  plugin: ({
    options,
    url,
    method,
    params,
    data
  }, context) => {
    const _context$componentCon = context.componentContext.getProps(),
      {
        options: componentOptions,
        urlParams
      } = _context$componentCon,
      componentProps = _objectWithoutPropertiesLoose(_context$componentCon, _excluded$3);
    const targetParams = merge({}, componentOptions, pick(componentProps, ['url', 'method', 'params', 'data']), options, {
      url,
      method,
      params,
      data,
      urlParams
    });
    const output = Object.assign({}, targetParams);
    if (typeof urlParams === 'object' && Object.keys(urlParams).length > 0 && typeof output.url === 'string') {
      output.url = output.url.replace(/{([\s\S]+?)}/g, (match, name) => {
        return urlParams.hasOwnProperty(name) ? urlParams[name] : match;
      });
    }
    context.componentContext.setRequestParams(targetParams);
    return output;
  }
};

const _excluded$2 = ["isLocal"];
class Cache {
  static now() {
    return new Date().getTime();
  }
  constructor(options) {
    const {
      ttl,
      maxLength,
      isLocal,
      localName
    } = Object.assign({}, options);
    this.ttl = ttl || 0;
    this.data = {};
    this.cacheNameMapping = {};
    this.maxLength = maxLength || 1000;
    this.isLocal = localName ? true : isLocal;
    this.localName = localName;
    this._load();
  }
  _load() {
    if (!this.isLocal) {
      return;
    }
    try {
      const dataString = window.localStorage && window.localStorage.getItem(this.localName || Cache.KEY_NAME);
      if (!dataString) {
        return;
      }
      const dataObj = JSON.parse(dataString);
      Object.keys(dataObj).forEach(key => {
        dataObj[key].isLocal = true;
      });
      this.data = dataObj;
    } catch (e) {}
  }
  _save() {
    if (!this.isLocal) {
      return;
    }
    const keys = Object.keys(this.data);
    Promise.all(keys.map(key => this.data[key].value)).then(data => {
      const output = {};
      Object.keys(this.data).forEach((key, index) => {
        const _this$data$key = this.data[key],
          {
            isLocal
          } = _this$data$key,
          props = _objectWithoutPropertiesLoose(_this$data$key, _excluded$2);
        if (isLocal === true) {
          output[key] = Object.assign({}, props, {
            value: data[index]
          });
        }
      });
      window.localStorage && window.localStorage.setItem(this.localName || Cache.KEY_NAME, JSON.stringify(output));
    });
  }
  get(key) {
    const obj = this.data[key];
    let val = null;
    if (obj) {
      val = obj.value;
      if (obj.expires && Cache.now() >= obj.expires) {
        val = null;
        delete this.data[key];
      }
    }
    this._save();
    return val;
  }
  delByCacheName(cacheName) {
    if (this.cacheNameMapping[cacheName]) {
      this.cacheNameMapping[cacheName].forEach(key => {
        this.del(key);
      });
      this.cacheNameMapping[cacheName].clear();
    }
  }
  del(key) {
    const oldValue = this.get(key);
    delete this.data[key];
    this._save();
    return oldValue;
  }
  put(key, val, options) {
    let {
      ttl,
      isLocal,
      cacheName
    } = options;
    if (ttl === undefined) {
      ttl = this.ttl;
    }
    let oldValue = this.del(key);
    if (val !== null) {
      const keys = Object.keys(this.data);
      if (keys.length >= this.maxLength) {
        const delKey = keys.sort((a, b) => {
          return this.data[a].createTime - this.data[b].createTime;
        })[0];
        delete this.data[delKey];
      }
      const now = Cache.now();
      this.data[key] = {
        expires: ttl === 0 ? null : now + ttl,
        value: val,
        createTime: now,
        isLocal
      };
      if (cacheName && !this.cacheNameMapping[cacheName]) {
        this.cacheNameMapping[cacheName] = new Set();
      }
      if (cacheName) {
        this.cacheNameMapping[cacheName].add(key);
      }
    }
    this._save();
    return oldValue;
  }
  clean() {
    this.data = {};
    this._save();
  }
}
Cache.KEY_NAME = 'REACT_FETCH_CACHE';

const globalParams = {
  cache: new Cache({
    ttl: 1000 * 60 * 10,
    maxLength: 1000,
    isLocal: true
  }),
  ajax: () => {
    throw new Error('默认的axios配置已经移除，请首先在preset里面配置');
  },
  loading: null,
  error: null,
  empty: null,
  pagination: {
    initCurrent: 1,
    pageSize: 10,
    transform: params => params
  },
  transformResponse: response => {
    const {
      data
    } = response;
    response.data = {
      code: data.code,
      msg: data.msg,
      results: data.results
    };
    return response;
  }
};
const getCache = () => globalParams.cache;
var preset = (newOptions => {
  return Object.assign(globalParams, newOptions);
});

var cache = {
  id: 'cache',
  plugin: ({
    force
  }, context) => {
    const {
      cache
    } = context.componentContext.getProps();
    const token = context.componentContext.getRequestToken();
    if (force === true || !cache) {
      return;
    }
    const cacheKey = (cache === true ? '' : cache) + token;
    const cacheData = globalParams.cache.get(cacheKey);
    if (!cacheData) {
      return;
    }
    return Promise.resolve(cacheData);
  }
};

var request$1 = {
  id: 'request',
  plugin: ({
    force
  }, context) => {
    if (context.outputStack['loader'] || context.errorStack['loader']) {
      return;
    }
    if (context.outputStack['cache']) {
      return context.outputStack['cache'];
    }
    const token = context.componentContext.getRequestToken();
    context.componentContext.requestParams = context.outputStack['params'];
    const {
      ajax
    } = context.componentContext.getProps();
    const requestPromise = force !== true && get(context.globalContext.pendingRequest, token) || (ajax || globalParams.ajax)(context.outputStack['params']);
    context.globalContext.pendingRequest = Object.assign({}, context.globalContext.pendingRequest, {
      [token]: requestPromise
    });
    return requestPromise.then(data => {
      delete context.globalContext.pendingRequest[token];
      return data;
    });
  },
  dependencies: ['params']
};

var cacheRecord = {
  id: 'cache-record',
  plugin: (props, context) => {
    const {
      cache,
      isLocal,
      ttl
    } = context.componentContext.getProps();
    const token = context.componentContext.getRequestToken();
    const cacheName = cache === true ? '' : cache;
    const cacheKey = cacheName + token;
    if (cache && (context.outputStack['request'] || context.outputStack['loader']) && context.outputStack['output-data'] !== void 0) {
      globalParams.cache.put(cacheKey, context.outputStack['request'] || context.outputStack['loader'], {
        ttl,
        isLocal,
        cacheName
      });
    }
  },
  dependencies: ['cache', 'request', 'output-data']
};

var transformResponse = {
  id: 'transform-response',
  plugin: (props, context) => {
    const {
      transformResponse,
      ignoreSuccessState,
      options
    } = context.componentContext.getProps();
    const response = context.outputStack['request'];
    if (!response) {
      return;
    }
    if (ignoreSuccessState === true) {
      return response;
    }
    return (transformResponse || globalParams.transformResponse)(Object.assign({}, response));
  },
  dependencies: ['request']
};

var outputData = {
  id: 'output-data',
  plugin: (props, context) => {
    const {
      options,
      onRequestSuccess,
      ignoreSuccessState,
      transformData
    } = context.componentContext.getProps();
    if (Object.keys(context.errorStack).length > 0) {
      return;
    }
    const responseData = context.output.data;
    if (ignoreSuccessState !== true && responseData.code !== 200) {
      const error = new Error(responseData.msg);
      error.responseData = responseData;
      throw error;
    }
    const output = (output => typeof transformData === 'function' ? transformData(output, context.outputStack['params']) : output)((ignoreSuccessState !== true ? responseData.results : responseData) || {});
    onRequestSuccess && onRequestSuccess(output);
    return output;
  },
  dependencies: ['request']
};

var loadMore = {
  id: 'load-more',
  plugin: ({
    type,
    callback
  }, context) => {
    if (type === 'load-more' && typeof callback === 'function') {
      const {
        fetchData
      } = context.componentContext.getState();
      return callback(fetchData, context.output);
    }
  },
  dependencies: ['output-data']
};

var complete = {
  id: 'complete',
  plugin: ({
    type
  }, context) => {
    const {
      setIsLoading,
      setIsComplete,
      setFetchData
    } = context.componentContext;
    setFetchData(context.output);
    if (type === 'refresh') {
      setIsLoading(false);
    }
    setIsComplete(true);
  }
};

var error = {
  id: 'error',
  plugin: (props, context) => {
    const {
      onRequestError,
      onRequestComplete
    } = context.componentContext.getProps();
    const {
      setError
    } = context.componentContext;
    if (Object.keys(context.errorStack).length > 0) {
      const errorMsg = Object.values(context.errorStack).map(e => {
        return e.message;
      }).filter(msg => !!msg).join('\n');
      onRequestError && onRequestError(Object.values(context.errorStack));
      setError(errorMsg || '请求发生错误');
    } else {
      setError(null);
    }
    onRequestComplete && onRequestComplete(context);
  }
};

const plugins = [start, params, cache, loader, request$1, transformResponse, outputData, cacheRecord, loadMore, complete, error];
const globalContext = {};
const createRunner = componentContext => {
  return async props => {
    props = Object.assign({}, props);
    const allPlugins = plugins;
    const pluginContext = {
      props,
      globalContext,
      componentContext,
      requestContext: {},
      outputStack: {},
      errorStack: {},
      runPath: [],
      output: null
    };
    const {
      debug
    } = componentContext.getProps();
    for (let currentPlugin of allPlugins) {
      try {
        // 依赖检查，依赖插件没有成功运行，跳过插件
        if ((currentPlugin.dependencies || []).find(name => {
          return pluginContext.runPath.indexOf(name) === -1;
        })) {
          continue;
        }
        if (debug === true) {
          console.info('react-fetch:plugin-' + currentPlugin.id + ':start', pluginContext);
        }
        const output = await currentPlugin.plugin(props, pluginContext);
        if (debug === true) {
          console.info('react-fetch:plugin-' + currentPlugin.id + ':complete', output);
        }
        pluginContext.outputStack[currentPlugin.id] = output;
        pluginContext.output = output !== void 0 ? output : pluginContext.output;
        pluginContext.runPath.push(currentPlugin.id);
      } catch (e) {
        console.error(e);
        pluginContext.errorStack[currentPlugin.id] = e;
      }
    }
    return pluginContext;
  };
};

const getRequestToken = props => objectHash(pick(props, ['url', 'params', 'method', 'data', 'urlParams', 'options']), {
  algorithm: 'md5',
  encoding: 'base64'
});

const useFetch = fetcherOptions => {
  const props = Object.assign({
    auto: true,
    updateType: 'reload'
  }, fetcherOptions);
  const propsRef = useRef(props);
  propsRef.current = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [fetchData, setFetchData] = useState(null);
  const [error, setError] = useState(null);
  const [requestParams, setRequestParams] = useState({});
  const stateRef = useRef({
    isLoading,
    isComplete,
    fetchData,
    error,
    requestParams
  });
  stateRef.current = {
    isLoading,
    isComplete,
    fetchData,
    error,
    requestParams
  };
  const requestToken = getRequestToken(props);
  const requestTokenRef = useRef(requestToken);
  requestTokenRef.current = requestToken;
  const pluginRunnerRef = useRef();
  useMemo(() => {
    pluginRunnerRef.current = createRunner({
      getProps: () => propsRef.current,
      getRequestToken: () => requestTokenRef.current,
      getState: () => stateRef.current,
      requestParams,
      setRequestParams,
      setFetchData,
      setError,
      setIsComplete,
      setIsLoading
    });
  }, []);
  const send = (sendProps, force = true) => {
    return pluginRunnerRef.current(Object.assign({}, sendProps, {
      force
    }));
  };
  const refresh = (sendProps, force = true) => {
    return pluginRunnerRef.current(Object.assign({}, sendProps, {
      force,
      type: 'refresh'
    }));
  };
  const reload = (sendProps, force = true) => {
    return pluginRunnerRef.current(Object.assign({}, sendProps, {
      force,
      type: 'reload'
    }));
  };
  const loadMore = (sendProps, callback, force = true) => {
    return pluginRunnerRef.current(Object.assign({}, sendProps, {
      force,
      callback,
      type: 'load-more'
    }));
  };
  const apiRef = useRef({});
  apiRef.current = {
    send,
    refresh,
    reload,
    loadMore,
    setData: setFetchData
  };
  useEffect(() => {
    if (propsRef.current.auto) {
      if (stateRef.current.isComplete) {
        apiRef.current.send({
          type: propsRef.current.updateType
        }, false);
      } else {
        apiRef.current.refresh({}, false);
      }
    }
  }, [requestToken]);
  return {
    isLoading,
    isComplete,
    data: fetchData,
    error,
    send,
    refresh,
    reload,
    loadMore,
    setData: setFetchData,
    requestParams
  };
};

const _excluded$1 = ["component", "render", "loading", "isEmpty", "empty", "error"];
const Fetch = forwardRef((_ref, ref) => {
  let {
      component,
      render,
      loading,
      isEmpty,
      empty,
      error: errorComponent
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  const {
    isLoading,
    isComplete,
    data,
    requestParams,
    error,
    send,
    refresh,
    reload,
    loadMore,
    setData
  } = useFetch(props);
  const fetchPropsList = ['url', 'params', 'urlParams', 'method', 'data', 'cache', 'ttl', 'isLocal', 'auto', 'loader', 'options', 'updateType', 'onRequestError', 'onRequestSuccess', 'onRequestComplete', 'onRequestStart', 'debug', 'ajax', 'transformData', 'transformResponse'];
  const otherProps = omit(props, fetchPropsList);
  const fetchProps = pick(props, fetchPropsList);
  useImperativeHandle(ref, () => {
    return {
      isLoading,
      isComplete,
      data,
      requestParams,
      error,
      send,
      refresh,
      reload,
      loadMore,
      setData
    };
  });
  if (isLoading) {
    return loading === void 0 ? globalParams.loading : loading;
  }
  if (error) {
    const _error = errorComponent === void 0 ? globalParams.error : errorComponent;
    if (typeof _error === 'function') {
      return _error(error);
    }
    return _error;
  }
  if (!isComplete && !data) {
    return null;
  }
  if (typeof isEmpty === 'function' ? isEmpty(data, requestParams) : !data) {
    return empty === void 0 ? globalParams.empty : empty;
  }
  if (component) {
    const FetchComponent = component;
    return /*#__PURE__*/React.createElement(FetchComponent, _extends({}, otherProps, {
      fetchProps: fetchProps,
      isComplete: isComplete,
      data: data,
      refresh: refresh,
      reload: reload,
      setData: setData,
      loadMore: loadMore,
      send: send,
      requestParams: requestParams
    }));
  }
  if (render) {
    return render(_extends({}, otherProps, {
      fetchProps,
      isComplete,
      data,
      refresh,
      reload,
      setData,
      loadMore,
      send,
      requestParams
    }));
  }
  throw new Error('请传入component参数或者render参数');
});

const withFetch = WrappedComponent => {
  return forwardRef((props, ref) => /*#__PURE__*/React.createElement(Fetch, _extends({}, props, {
    component: WrappedComponent,
    ref: ref
  })));
};
const createWithFetch = params => WrappedComponent => {
  const FetchComponent = withFetch(WrappedComponent);
  return forwardRef((props, ref) => /*#__PURE__*/React.createElement(FetchComponent, _extends({}, merge({}, params, props), {
    ref: ref
  })));
};

const _excluded = ["onRequestParamsChange", "onRequestDataChange", "onError", "onIsCompleteChange", "onIsLoadingChange"];
const request = props => {
  const _Object$assign = Object.assign({}, props, {
      options: Object.assign({}, props == null ? void 0 : props.options)
    }, {
      type: 'refresh',
      ignoreSuccessState: true
    }),
    {
      onRequestParamsChange,
      onRequestDataChange,
      onError,
      onIsCompleteChange,
      onIsLoadingChange
    } = _Object$assign,
    requestProps = _objectWithoutPropertiesLoose(_Object$assign, _excluded);
  return createRunner({
    getProps: () => requestProps,
    getRequestToken: () => getRequestToken(requestProps),
    requestParams: {},
    setRequestParams: (...args) => onRequestParamsChange && onRequestParamsChange(...args),
    setFetchData: (...args) => onRequestDataChange && onRequestDataChange(...args),
    setError: (...args) => onError && onError(...args),
    setIsComplete: (...args) => onIsCompleteChange && onIsCompleteChange(...args),
    setIsLoading: (...args) => onIsLoadingChange && onIsLoadingChange(...args)
  })({}).then(({
    outputStack
  }) => {
    return outputStack['transform-response'];
  });
};

export { Cache, createWithFetch, Fetch as default, getCache, preset, request, useFetch, withFetch };
//# sourceMappingURL=index.modern.js.map
